/** @jsxImportSource @emotion/react */
"use client";

import type { NiceModalHandler } from "@ebay/nice-modal-react";
import type { Interpolation } from "@emotion/react";
import type * as RadixUIDialog from "@radix-ui/react-dialog";
import type { ComponentProps, ReactNode } from "react";

import {
  numericPercentages,
  spacing,
  zIndices,
} from "../../../../../global/emotion";
import { useIsDesktopViewport } from "../../../../hooks/useIsDesktopViewport";
import { cssMediaQueryMinWidthTablet } from "../../../../lib/constants/emotionMediaQueries.constants";
import { createModal, useModal } from "../../../../niceModal";
import { ScrollArea } from "../../../blocks/other/ScrollArea/ScrollArea.emotion";
import type { FullscreenModalProps } from "../FullscreenModal/FullscreenModal.emotion";
import { FullscreenModal } from "../FullscreenModal/FullscreenModal.emotion";
import type { ModalProps } from "../Modal/Modal.emotion";
import { Modal } from "../Modal/Modal.emotion";

const viewportStyle: Interpolation = {
  height: `calc(100dvh - ${spacing.footer})`,
  maxWidth: numericPercentages[100],
  width: numericPercentages[100],
  [cssMediaQueryMinWidthTablet]: {
    padding: spacing.xl,
    height: "auto",
    maxWidth: "480px",
  },
};
const contentStyle: Interpolation = {
  width: "fit-content",
  zIndex: zIndices.modalOverModal,
};
const overlayStyle: Interpolation = { zIndex: zIndices.overlayOverModal };

type FullscreenModalModalProps = ComponentProps<typeof RadixUIDialog.Root> & {
  trigger?: ReactNode;
  fullScreenModalProps?: FullscreenModalProps;
  modalProps?: ModalProps;
  className?: string;
};

export const FullscreenModalModal = ({
  open,
  onOpenChange,
  fullScreenModalProps = {
    showCloseButton: true,
  },
  modalProps = {
    contentCSS: contentStyle,
    overlayCSS: overlayStyle,
  },
  children,
  trigger,
  className = "",
}: FullscreenModalModalProps) => {
  const isDesktopViewport = useIsDesktopViewport();

  const handleOpenChange = (open) => {
    if (onOpenChange) {
      onOpenChange(open);
    }
  };

  const ModalEl = ({ children }: { children: React.ReactElement<any> }) => {
    if (isDesktopViewport)
      return (
        <Modal
          open={open}
          onOpenChange={handleOpenChange}
          trigger={trigger}
          {...modalProps}
        >
          {children}
        </Modal>
      );

    return (
      <FullscreenModal
        open={open}
        onOpenChange={handleOpenChange}
        modal={false}
        trigger={trigger}
        {...fullScreenModalProps}
      >
        {children}
      </FullscreenModal>
    );
  };

  return (
    <ModalEl>
      <ScrollArea
        viewportCSS={viewportStyle}
        masksColor={isDesktopViewport ? "onOverlayBackground" : "background"}
        maskBottom
        maskTop
        masksSize="xs"
        className={className}
      >
        {children}
      </ScrollArea>
    </ModalEl>
  );
};

export function createFullscreenModal<T extends {}>(
  children: (
    props: T,
    modal: NiceModalHandler<Record<string, unknown>>,
  ) => React.ReactNode,
) {
  return createModal<T>((props) => {
    const modal = useModal();

    return (
      <FullscreenModalModal
        open={modal.visible}
        onOpenChange={(open) => (open ? modal.show() : modal.hide())}
      >
        {children(props, modal)}
      </FullscreenModalModal>
    );
  });
}
